import { MessageCircle } from "lucide-react";
import { WHATSAPP_NUMBER } from "@/lib/socialLinks";
import salon3 from "@/assets/salon/salon-3.jpg.asset.json";
import salon5 from "@/assets/salon/salon-5.jpg.asset.json";
import salon4 from "@/assets/salon/salon-4.jpg.asset.json";
import salon2 from "@/assets/salon/salon-2.jpg.asset.json";

const buildWhatsappUrl = (message: string) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

type Service = {
  title: string;
  description: string;
  image: { url: string };
  alt: string;
  message: string;
};

const SERVICES: Service[] = [
  {
    title: "Wig Fittings & Consultations",
    description:
      "Private fittings for human-hair and synthetic wigs, including professional colour matching and styling to ensure a natural, confident look.",
    image: salon3,
    alt: "Wig fittings & consultations – Plymhair Salon, Plymouth",
    message:
      "Hi Plymhair — I'd like to book a wig fitting / consultation at the Plymouth salon.",
  },
  {
    title: "Hair Extensions",
    description:
      "Tape-in, micro-ring, weft and clip-in extensions — expertly fitted by a specialist to add length, volume or dimension seamlessly to your natural hair.",
    image: salon5,
    alt: "Hair extensions – Plymhair Salon, Plymouth",
    message:
      "Hi Plymhair — I'd like to book a hair extension appointment at the Plymouth salon.",
  },
  {
    title: "NHS Wig Appointments",
    description:
      "Approved NHS wig supplier (ENG-A & ENG-B vouchers accepted). Compassionate, private care in a calm space — to help you find the right solution during treatment.",
    image: salon4,
    alt: "NHS wig appointments – Plymhair Salon, Plymouth",
    message:
      "Hi Plymhair — I'd like to book an NHS wig appointment at the Plymouth salon.",
  },
  {
    title: "Maintenance & Restyling",
    description:
      "Professional refit, wash, restyle and refresh services to keep your wig or extensions looking their absolute best for longer.",
    image: salon2,
    alt: "Maintenance & restyling – Plymhair Salon, Plymouth",
    message:
      "Hi Plymhair — I'd like to book a maintenance / restyling appointment at the Plymouth salon.",
  },
];

const SalonServices = () => {
  return (
    <section className="py-16 sm:py-20 md:py-28 bg-background">
      <div className="container max-w-6xl px-4 sm:px-6">
        <div className="text-center mb-14 md:mb-20 lg:mb-24 max-w-2xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 md:mb-5 leading-tight">
            What we do in the salon
          </h2>
          <p className="font-body text-sm sm:text-base md:text-lg text-muted-foreground italic font-light">
            Every appointment is one-to-one, unhurried, and tailored to you.
          </p>
        </div>

        <div className="space-y-16 sm:space-y-20 md:space-y-28 lg:space-y-32">
          {SERVICES.map((service, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <article
                key={service.title}
                className={`flex flex-col ${
                  reverse ? "md:flex-row-reverse" : "md:flex-row"
                } items-center gap-6 sm:gap-8 md:gap-12 lg:gap-20`}
              >
                <div className="w-full md:w-1/2 overflow-hidden bg-secondary rounded-sm">
                  <img
                    src={service.image.url}
                    alt={service.alt}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 hover:scale-[1.03]"
                  />
                </div>
                <div
                  className={`w-full md:w-1/2 flex flex-col space-y-4 md:space-y-5 text-left ${
                    reverse ? "md:text-right md:items-end" : ""
                  }`}
                >
                  <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-semibold text-foreground leading-tight">
                    {service.title}
                  </h3>
                  <p className="font-body text-muted-foreground leading-relaxed text-sm sm:text-base md:text-lg max-w-prose">
                    {service.description}
                  </p>
                  <div>
                    <a
                      href={buildWhatsappUrl(service.message)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center sm:justify-start gap-2 bg-primary text-primary-foreground font-body text-xs font-semibold tracking-widest uppercase px-6 sm:px-7 py-3 rounded-sm hover:bg-primary/90 transition-colors w-full sm:w-auto"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Book on WhatsApp
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SalonServices;
